import React, { useEffect } from 'react';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { SiteModeProvider, useSiteMode } from '@/context/SiteModeContext';
import Auth from '@/pages/Auth';
import PatternEngine from '@/pages/PatternEngine';
import SiteModeBanner from '@/components/SiteModeBanner';
import { Toaster } from '@/components/ui/sonner';

function ResizeObserverOverlayGuard() {
  useEffect(() => {
    const isResizeObserverNoise = (message = '') => (
      String(message || '').includes('ResizeObserver loop completed with undelivered notifications')
      || String(message || '').includes('ResizeObserver loop limit exceeded')
    );

    const originalConsoleError = console.error;
    const originalConsoleWarn = console.warn;

    console.error = (...args) => {
      if (args.some((arg) => isResizeObserverNoise(arg?.message || arg))) return;
      originalConsoleError(...args);
    };

    console.warn = (...args) => {
      if (args.some((arg) => isResizeObserverNoise(arg?.message || arg))) return;
      originalConsoleWarn(...args);
    };

    const handleWindowError = (event) => {
      if (!isResizeObserverNoise(event?.message)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    };

    const handleUnhandledRejection = (event) => {
      const reasonMessage = event?.reason?.message || event?.reason || '';
      if (!isResizeObserverNoise(reasonMessage)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    };

    window.addEventListener('error', handleWindowError, true);
    window.addEventListener('unhandledrejection', handleUnhandledRejection, true);

    return () => {
      window.removeEventListener('error', handleWindowError, true);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection, true);
      console.error = originalConsoleError;
      console.warn = originalConsoleWarn;
    };
  }, []);

  return null;
}

function AppAuthenticatedContent() {
  const { siteMode } = useSiteMode();
  const isDevMode = siteMode === 'dev';

  return (
    <div className={isDevMode ? 'min-h-screen bg-red-50/35' : 'min-h-screen bg-white'} data-testid="app-shell">
      {isDevMode && <SiteModeBanner />}
      <PatternEngine />
    </div>
  );
}

function AppContent() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Auth />;
  }

  return (
    <SiteModeProvider>
      <AppAuthenticatedContent />
    </SiteModeProvider>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <ResizeObserverOverlayGuard />
      <AppContent />
      <Toaster position="top-right" richColors closeButton />
    </AuthProvider>
  );
}
